// LIBRARY IMPORTS
import { FC, useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { Dialog, Typography, Button, CircularProgress, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme, makeStyles } from "@material-ui/core";
import { mdiCheck, mdiChevronDown, mdiChevronRight } from '@mdi/js';
import { Icon } from '@mdi/react'
import moment from 'moment';

// LOCAL IMPORTS
import { RecibosFlexiblesGateway } from 'gateways/recibo-flexibles.gateway';
import { FilterDataContext } from './filters/filter.data.context';
import TextNumber from 'components/text-number';
import IoC from 'contexts/ioc.context';
import Term from 'components/term';
import ConceptoIcon from 'components/concepto.icon';
import { download } from 'utils/download';
import { translate } from 'utils/i18n';

// STYLES
import styles from '../../shared/tributo.detail.styles';
import { IReciboFlexible, IReciboFlexiblePago } from 'gateways/recibo-flexibles.interfaces ';
import { LiteralsContext } from 'containers/shared/literals';
import { AlertsContext } from 'contexts/alerts.context';

moment.locale('es-es');

const useStyles = makeStyles(styles);

export interface IProps {
    open: boolean;
    recibo: IReciboFlexible;
    onClose: () => void;
}

const ReciboDetail: FC<IProps> = (props) => {
    const { open, recibo, onClose } = props;

    // HOOKS
    const filterData = useContext(FilterDataContext);
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [pagosFlexiblesList, setPagosFlexiblesList] = useState<IReciboFlexiblePago[]>([])
    const terms = useContext(LiteralsContext);
    const [, alertsDispatch] = useContext(AlertsContext)

    // GATEWAYS
    const ioc = useContext(IoC);
    const gateway = useMemo(() => ioc.get(RecibosFlexiblesGateway) as RecibosFlexiblesGateway, [ioc]);

    const importePendiente = recibo.importeRecibo - recibo.importeFlexibleAcumulado;

    // BOOLEAN STATES
    const [loading, setLoading] = useState(false);
    const [openDatosPagoFlexible, setOpenDatosPagoFlexible,] = useState(false)
    const [loadingPrint, setLoadingPrint] = useState(false);

    // ----------------------------- HANDLERS -----------------------------

    const handleClose = useCallback(() => {
        onClose()
        setPagosFlexiblesList([]);
    }, [onClose]);

    const handleImporte = () => {
        let importe = recibo.importeFlexibleAcumulado;
        return importe;
    }

    const handleDescargar = async () => {
        setLoadingPrint(true)
        const result = await gateway.print('', '099', pagosFlexiblesList[0].idModelo);
        download(result, alertsDispatch,
            translate('PasarelaPago', 'No es posible descargar el justificante', terms),
            translate('Global', 'BloqueoPantalla', terms));
        setLoadingPrint(false)
    }

    const handleTogglePagoFlexible = useCallback(() => {
        setOpenDatosPagoFlexible(!openDatosPagoFlexible)
    }, [openDatosPagoFlexible]);


    const getListPagosFlexibles = async () => {
        setLoading(true)
        const result = await gateway.getListadoPagosFlexibles(recibo.idReciboOrigen);
        //console.log('lista Pago cuenta R_Detail', result);
        // result.sort( (a,b) => a.fechaEnvio < b.fechaEnvio ? -1 :1)
        console.log('lista sort', result);
        setPagosFlexiblesList(result);
        setLoading(false)
        // setImportePagadoAPlazos(result.reduce((acc, pago) => acc + pago.importe, 0));
    };



    // ----------------------------- USE EFFECTS -----------------------------

    useEffect(() => {
        if (open) {
            setLoading(true)
            getListPagosFlexibles()
        }
        else {
            setPagosFlexiblesList([]);
        }
        setLoading(false);
    }, [open])




    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleClose}
            maxWidth={"md"}
            fullWidth={true}
        >
            {/* INFORMACIÓN TRIBUTO Y ETIQUETA */}

            <DialogTitle>
                <Term component="CarteroVirtual" text="Información tributo" />
            </DialogTitle>
            {loading
                        ?
                        <div className={classes.centerContent}>
                            <CircularProgress size={75} />
                        </div>
                        :<>
            <DialogContent>
                {
                    
                        <div className={classes.main}>
                            <div>
                                {/* {JSON.stringify(pagosFlexiblesList)} */}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '10px' }}>
                                <div>
                                    <Typography className={[classes.text, classes.bold].join(' ')} style={{ marginBottom: '10px' }}>{(recibo.organismo ? recibo.organismo.nombre + ' | ' : '') + new Date().getFullYear()}</Typography>
                                    <div>
                                        <Typography className={classes.text}><Term component="Global" text="Nif" />: <span className={classes.bold}>{recibo.sujeto.nif}</span></Typography>
                                        <Typography className={classes.text}><Term component="Global" text="Nombre" />: <span className={classes.bold}>{recibo.sujeto.nombre}</span></Typography>
                                    </div>
                                </div>
                                <div>
                                    {
                                        recibo.direccionTributaria ?
                                            <Typography className={classes.text}>
                                                <Term component="CarteroVirtual" text={recibo.concepto.idTipo === 7 || recibo.concepto.codigo === "IG" ? "lugar_infraccion" : "Dirección tributaria"} />: <br /><span className={classes.bold}>{recibo.direccionTributaria}</span>
                                            </Typography>
                                            : null
                                    }
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div className={classes.imagenContainer}>
                                        <ConceptoIcon concepto={recibo.concepto.idTipo} codigo={recibo.concepto.codigo} />
                                    </div>
                                    <div>
                                        <Typography className={classes.text}><Term component="CarteroVirtual" text="Objeto" />: </Typography><span className={classes.bold}>{recibo.objeto}</span>
                                    </div>
                                </div>
                                <div className={classes.section} style={{ display: 'flex', alignItems: 'center', width: '100%' }} >
                                    <div style={{ flex: 1 }}>
                                        <Typography className={[classes.text].join(' ')}>
                                            <Term component="CarteroVirtual" text="Pago_flexible_importe" />:
                                        </Typography>
                                        <TextNumber
                                            className={[classes.text, classes.bold].join(' ')}
                                            value={recibo.importeRecibo}
                                            options={{ isMoney: true, minDecimalLength: 2, maxDecimalLength: 2 }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <Typography className={[classes.text].join(' ')}>
                                            <Term component="CarteroVirtual" text="Importe pagado" />:
                                        </Typography>
                                        <TextNumber
                                            className={[classes.text, classes.bold].join(' ')}
                                            value={handleImporte()}
                                            options={{ isMoney: true, minDecimalLength: 2, maxDecimalLength: 2 }} />
                                    </div>
                                </div>
                            </div>

                            {recibo.importeFlexibleAcumulado > 0 && (
                                <>
                                    <div className={classes.pcBlueRim}>
                                        {/* TÍTULO INFORMACIÓN PAGO FLEXIBLE */}
                                        <div
                                            onClick={handleTogglePagoFlexible}
                                            className={classes.text}
                                            style={{
                                                width: "100%",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <div style={{ display: "flex", gap: 5 }}>
                                                <Typography className={[classes.bold, classes.pcBlueTitle].join(" ")}>
                                                    <Term component="CarteroVirtual" text="+ informacion pago flexible" />
                                                </Typography>
                                            </div>
                                            <Icon
                                                path={openDatosPagoFlexible ? mdiChevronDown : mdiChevronRight}
                                                title="Pago flexible"
                                                size={1}
                                                color={"#006993"}
                                                className={[classes.iconPC, classes.pcBlueTitle].join(" ")}
                                            />
                                        </div>

                                        {/* DESPLEGABLE PAGO FLEXIBLE*/}
                                        {openDatosPagoFlexible && (
                                            <div style={{ marginTop: 10 }} className={classes.pac_detail}>
                                                <Typography style={{ fontSize: 15 }}>
                                                    <Term component="CarteroVirtual" text="PagosRealizados" />:
                                                </Typography>
                                                

                                                {/* PAGOS REALIZADOS */}
                                                <div
                                                    style={{
                                                        padding: 20,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 5,
                                                    }}
                                                >
                                                    {pagosFlexiblesList.map(( pagos , index) => (
                                                        <div
                                                            key={index}
                                                            style={{
                                                                display: "flex",
                                                                // justifyContent: "space-between",
                                                                alignItems: "center",
                                                                marginLeft: 30,
                                                                marginRight: 30,
                                                            }}
                                                        >
                                                            {/* poner fecha */}
                                                            <Typography style={{ fontWeight: "bold", fontSize: 13, marginRight: 65  }}>
                                                               {moment(pagos.fAlta).format('DD/MM/YYYY')}
                                                            </Typography>

                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 10,
                                                                }}
                                                            >
                                                                <TextNumber
                                                                    component="span"
                                                                    className={classes.fontS}
                                                                    value={pagos.importe}
                                                                    options={{
                                                                        isMoney: true,
                                                                        minDecimalLength: 2,
                                                                        maxDecimalLength: 2,
                                                                    }}
                                                                />
                                                                <Icon color={'green'} size={0.6} path={mdiCheck} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* CONTAINER IMPORTES PAGOS FLEXIBLES */}
                                                <div className={classes.pac_detail_result}>
                                                    <div>
                                                        {/* PLAZOS ENVIADOS */}
                                                        <div
                                                            style={{ display: "flex", alignItems: "center", gap: 10 }}
                                                        >
                                                            <Typography style={{ fontSize: 15, width: "20%" }}>
                                                                <Term
                                                                    component="CarteroVirtual"
                                                                    text="Importe pagado"
                                                                />
                                                                :
                                                            </Typography>
                                                            <TextNumber
                                                                component="span"
                                                                className={[classes.fontM, classes.bold].join(" ")}
                                                                value={recibo.importeFlexibleAcumulado}
                                                                options={{
                                                                    isMoney: true,
                                                                    minDecimalLength: 2,
                                                                    maxDecimalLength: 2,
                                                                }}
                                                            />
                                                        </div>

                                                        {/* IMPORTE RECIBO */}
                                                        <div
                                                            style={{ display: "flex", alignItems: "center", gap: 10 }}
                                                        >
                                                            <Typography style={{ fontSize: 15, width: "20%" }}>
                                                                <Term
                                                                    component="CarteroVirtual"
                                                                    text="Pago_flexible_importe"
                                                                />
                                                                :
                                                            </Typography>
                                                            <TextNumber
                                                                component="span"
                                                                className={[classes.fontM, classes.bold].join(" ")}
                                                                value={recibo.importeRecibo}
                                                                options={{
                                                                    isMoney: true,
                                                                    minDecimalLength: 2,
                                                                    maxDecimalLength: 2,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* IMPORTE PENDIENTE */}
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 10,
                                                            marginTop: 15,
                                                        }}
                                                    >
                                                        <Typography style={{ fontSize: 15, width: "20%" }}>
                                                            <Term component="CarteroVirtual" text="Importe pendiente" />
                                                            :
                                                        </Typography>
                                                        <TextNumber
                                                            component="span"
                                                            className={[classes.fontM, classes.bold].join(" ")}
                                                            value={importePendiente}
                                                            options={{
                                                                isMoney: true,
                                                                minDecimalLength: 2,
                                                                maxDecimalLength: 2,
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}



                        </div>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="default" disabled={loadingPrint}>
                    <Term component="Global" text="Cerrar" />
                </Button>
                {
                recibo.importeFlexibleAcumulado > 0 ? (
                <Button onClick={handleDescargar} color="primary" disabled={loadingPrint} variant="outlined">
                    <Term component="Global" text="Imprimir" />
                </Button>) : <></>
                }

                {loadingPrint && <CircularProgress size={24} className={classes.buttonProgress} />}

            </DialogActions>
            </>}
        </Dialog>
    )
};

export default ReciboDetail;